/**
 * Record Outcome Modal
 * Modal for recording project outcome: Win, Blocker, or Lost
 */

import React, { useState, useEffect, useRef } from 'react';
import { signingAPI, projectFilesAPI } from '../../services/api';

const RecordOutcomeModal = ({ isOpen, onClose, projectId, onSuccess }) => {
  const [outcome, setOutcome] = useState(''); // 'win', 'blocker', 'lost'
  const [outcomeData, setOutcomeData] = useState({
    reason: '',
    notes: '',
    // Win-specific
    actualSignedAmount: '',
    commissionAmount: '',
    lockPricePaymentAmount: '',
    lockPricePaymentDate: new Date().toISOString().split('T')[0], // Default to today
    lockPricePaymentMethod: '',
    lockPriceReferenceNumber: '',
    // Blocker-specific
    blockerType: '',
    // Lost-specific
    lostReason: '',
    competitorName: ''
  });
  const [lockPriceReceipts, setLockPriceReceipts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [loadingAppointment, setLoadingAppointment] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch appointment when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      fetchAppointment();
    }
  }, [isOpen, projectId]);

  const fetchAppointment = async () => {
    setLoadingAppointment(true);
    setError(null);
    try {
      console.log('RecordOutcomeModal: Fetching appointment for projectId:', projectId);
      const response = await signingAPI.getByProject(projectId);
      console.log('RecordOutcomeModal: API response:', response);
      const appointments = response.data?.data || response.data;
      console.log('RecordOutcomeModal: Parsed appointments:', appointments);

      if (appointments && appointments.length > 0) {
        // Get the most recent scheduled appointment, or any appointment if none scheduled
        const scheduled = appointments.find(apt => apt.status === 'scheduled');
        const selected = scheduled || appointments[0];
        console.log('RecordOutcomeModal: Selected appointment:', selected);
        console.log('RecordOutcomeModal: Appointment status:', selected.status);
        setAppointmentId(selected.id);
      } else {
        console.log('RecordOutcomeModal: No appointments found in response');
        setError('No signing appointment found for this project. Please schedule a signing appointment first.');
      }
    } catch (err) {
      console.error('RecordOutcomeModal: Error fetching appointment:', err);
      // API interceptor wraps errors with { message, status, data }
      setError(err.message || 'Failed to load appointment details');
    } finally {
      setLoadingAppointment(false);
    }
  };

  if (!isOpen) return null;

  // Outcome configurations
  const outcomeConfig = {
    win: {
      color: 'green',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Record Win',
      description: 'Contract signed successfully!'
    },
    blocker: {
      color: 'yellow',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      title: 'Record Blocker',
      description: 'Temporary issue preventing signing'
    },
    lost: {
      color: 'red',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Record Lost',
      description: 'Client decided not to proceed'
    }
  };

  const config = outcome ? outcomeConfig[outcome] : null;

  // Validate inputs
  const validateInputs = () => {
    if (!outcome) {
      setError('Please select an outcome');
      return false;
    }

    if (outcome === 'win') {
      if (!outcomeData.actualSignedAmount || isNaN(outcomeData.actualSignedAmount)) {
        setError('Please enter a valid signed amount');
        return false;
      }
      if (!outcomeData.commissionAmount || isNaN(outcomeData.commissionAmount)) {
        setError('Please enter a valid commission amount');
        return false;
      }
      if (!outcomeData.lockPricePaymentAmount || isNaN(outcomeData.lockPricePaymentAmount)) {
        setError('Please enter a valid lock price payment amount');
        return false;
      }
    }

    if (outcome === 'blocker' && !outcomeData.blockerType) {
      setError('Please specify the blocker type');
      return false;
    }

    if (outcome === 'lost' && !outcomeData.lostReason) {
      setError('Please specify why the project was lost');
      return false;
    }

    return true;
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      return isValidType && isValidSize;
    });
    setLockPriceReceipts(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove file from selection
  const removeFile = (index) => {
    setLockPriceReceipts(prev => prev.filter((_, i) => i !== index));
  };

  // Handle submission
  const handleSubmit = async () => {
    if (!validateInputs()) return;

    if (!appointmentId) {
      setError('No appointment found. Please ensure a signing appointment is scheduled.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setUploadProgress(0);

    try {
      let response;

      if (outcome === 'win') {
        // Upload lock price receipts first if any
        if (lockPriceReceipts.length > 0) {
          for (let i = 0; i < lockPriceReceipts.length; i++) {
            const file = lockPriceReceipts[i];
            const formData = new FormData();
            formData.append('file', file);
            formData.append('projectId', projectId);
            formData.append('fileType', 'lock_price_receipt');

            await projectFilesAPI.upload(formData);
            setUploadProgress(Math.round(((i + 1) / lockPriceReceipts.length) * 100));
          }
        }

        response = await signingAPI.recordSigned(appointmentId, {
          signedBy: 'client',
          actualSignedAmount: outcomeData.actualSignedAmount ? parseFloat(outcomeData.actualSignedAmount) : null,
          commissionAmount: outcomeData.commissionAmount ? parseFloat(outcomeData.commissionAmount) : null,
          lockPricePaymentAmount: outcomeData.lockPricePaymentAmount ? parseFloat(outcomeData.lockPricePaymentAmount) : null,
          lockPricePaymentDate: outcomeData.lockPricePaymentDate || null,
          lockPricePaymentMethod: outcomeData.lockPricePaymentMethod || null,
          lockPriceReferenceNumber: outcomeData.lockPriceReferenceNumber || null,
          notes: outcomeData.notes
        });
      } else if (outcome === 'blocker') {
        response = await signingAPI.recordBlocker(appointmentId, {
          blockerType: outcomeData.blockerType,
          description: outcomeData.reason,
          notes: outcomeData.notes
        });
      } else if (outcome === 'lost') {
        response = await signingAPI.recordDeclined(appointmentId, {
          reason: outcomeData.lostReason,
          competitorName: outcomeData.competitorName,
          details: outcomeData.reason,
          notes: outcomeData.notes
        });
      }

      // Success callback
      if (onSuccess) {
        onSuccess({
          outcome,
          ...outcomeData,
          appointmentId,
          data: response?.data
        });
      }

      handleClose();
    } catch (err) {
      console.error('Error recording outcome:', err);
      setError(err.response?.data?.message || err.message || 'Failed to record outcome');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset and close
  const handleClose = () => {
    setOutcome('');
    setOutcomeData({
      reason: '',
      notes: '',
      actualSignedAmount: '',
      commissionAmount: '',
      lockPricePaymentAmount: '',
      lockPricePaymentDate: new Date().toISOString().split('T')[0],
      lockPricePaymentMethod: '',
      lockPriceReferenceNumber: '',
      blockerType: '',
      lostReason: '',
      competitorName: ''
    });
    setLockPriceReceipts([]);
    setUploadProgress(0);
    setError(null);
    setAppointmentId(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`sticky top-0 ${config ? `bg-${config.color}-600` : 'bg-gray-600'} text-white px-6 py-4 rounded-t-lg flex items-center justify-between`}
             style={config ? { backgroundColor: config.color === 'green' ? '#059669' : config.color === 'yellow' ? '#d97706' : '#dc2626' } : {}}>
          <div className="flex items-center gap-3">
            {config ? config.icon : (
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )}
            <h2 className="text-xl font-semibold">{config ? config.title : 'Record Outcome'}</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="text-white hover:text-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Loading appointment */}
          {loadingAppointment && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
              <p className="mt-4 text-gray-600">Loading appointment details...</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Outcome Selection */}
          {!outcome && !loadingAppointment && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Select Outcome *
              </label>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(outcomeConfig).map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setOutcome(key)}
                    disabled={submitting}
                    className={`p-6 border-2 border-gray-300 rounded-lg hover:border-${cfg.color}-500 hover:bg-${cfg.color}-50 transition-all disabled:opacity-50`}
                    style={{
                      borderColor: '#d1d5db',
                      ':hover': {
                        borderColor: cfg.color === 'green' ? '#10b981' : cfg.color === 'yellow' ? '#f59e0b' : '#ef4444',
                        backgroundColor: cfg.color === 'green' ? '#d1fae5' : cfg.color === 'yellow' ? '#fef3c7' : '#fee2e2'
                      }
                    }}
                  >
                    <div className={`text-${cfg.color}-600 flex justify-center mb-3`}
                         style={{ color: cfg.color === 'green' ? '#059669' : cfg.color === 'yellow' ? '#d97706' : '#dc2626' }}>
                      {cfg.icon}
                    </div>
                    <p className="font-semibold text-gray-900">{key.toUpperCase()}</p>
                    <p className="text-sm text-gray-600 mt-2">{cfg.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Selected outcome display */}
          {outcome && config && (
            <div className={`bg-${config.color}-50 border border-${config.color}-200 rounded-lg p-4`}
                 style={{
                   backgroundColor: config.color === 'green' ? '#d1fae5' : config.color === 'yellow' ? '#fef3c7' : '#fee2e2',
                   borderColor: config.color === 'green' ? '#a7f3d0' : config.color === 'yellow' ? '#fde68a' : '#fecaca'
                 }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div style={{ color: config.color === 'green' ? '#059669' : config.color === 'yellow' ? '#d97706' : '#dc2626' }}>
                    {config.icon}
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: config.color === 'green' ? '#065f46' : config.color === 'yellow' ? '#92400e' : '#991b1b' }}>
                      {config.title}
                    </p>
                    <p className="text-sm" style={{ color: config.color === 'green' ? '#047857' : config.color === 'yellow' ? '#b45309' : '#b91c1c' }}>
                      {config.description}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOutcome('')}
                  disabled={submitting}
                  className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  Change
                </button>
              </div>
            </div>
          )}

          {/* Win-specific fields */}
          {outcome === 'win' && (
            <div className="space-y-4 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900">Contract Details</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signed Amount (RM) *
                </label>
                <input
                  type="number"
                  value={outcomeData.actualSignedAmount}
                  onChange={(e) => setOutcomeData(prev => ({ ...prev, actualSignedAmount: e.target.value }))}
                  disabled={submitting}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commission Amount (RM) *
                </label>
                <input
                  type="number"
                  value={outcomeData.commissionAmount}
                  onChange={(e) => setOutcomeData(prev => ({ ...prev, commissionAmount: e.target.value }))}
                  disabled={submitting}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lock Price Payment (RM) *
                </label>
                <input
                  type="number"
                  value={outcomeData.lockPricePaymentAmount}
                  onChange={(e) => setOutcomeData(prev => ({ ...prev, lockPricePaymentAmount: e.target.value }))}
                  disabled={submitting}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                />
              </div>

              {/* Lock Price Payment Details */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    value={outcomeData.lockPricePaymentDate}
                    onChange={(e) => setOutcomeData(prev => ({ ...prev, lockPricePaymentDate: e.target.value }))}
                    disabled={submitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={outcomeData.lockPricePaymentMethod}
                    onChange={(e) => setOutcomeData(prev => ({ ...prev, lockPricePaymentMethod: e.target.value }))}
                    disabled={submitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                  >
                    <option value="">Select method...</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="online_banking">Online Banking</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference No.
                  </label>
                  <input
                    type="text"
                    value={outcomeData.lockPriceReferenceNumber}
                    onChange={(e) => setOutcomeData(prev => ({ ...prev, lockPriceReferenceNumber: e.target.value }))}
                    disabled={submitting}
                    placeholder="Transaction ref"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                  />
                </div>
              </div>

              {/* Lock Price Receipt Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lock Price Payment Receipt
                </label>
                <div
                  onClick={() => !submitting && fileInputRef.current?.click()}
                  className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600">Click to upload receipt</p>
                  <p className="text-xs text-gray-400 mt-1">Images or PDF, max 10MB each</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* Selected files list */}
                {lockPriceReceipts.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {lockPriceReceipts.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-gray-700 truncate max-w-[200px]">{file.name}</span>
                          <span className="text-xs text-gray-400">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          disabled={submitting}
                          className="text-red-500 hover:text-red-700 disabled:opacity-50"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload progress */}
                {submitting && lockPriceReceipts.length > 0 && uploadProgress > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                      <span>Uploading receipts...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Blocker-specific fields */}
          {outcome === 'blocker' && (
            <div className="space-y-4 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900">Blocker Details</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Blocker Type *
                </label>
                <select
                  value={outcomeData.blockerType}
                  onChange={(e) => setOutcomeData(prev => ({ ...prev, blockerType: e.target.value }))}
                  disabled={submitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent disabled:opacity-50"
                >
                  <option value="">Select blocker type...</option>
                  <option value="financing">Financing/Budget Issues</option>
                  <option value="timeline">Timeline Concerns</option>
                  <option value="design">Design Revisions Needed</option>
                  <option value="approval">Waiting for Approval</option>
                  <option value="documentation">Missing Documentation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Blocker Details *
                </label>
                <textarea
                  value={outcomeData.reason}
                  onChange={(e) => setOutcomeData(prev => ({ ...prev, reason: e.target.value }))}
                  disabled={submitting}
                  rows={4}
                  placeholder="Explain the blocker and what needs to happen to resolve it..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent disabled:opacity-50"
                />
              </div>
            </div>
          )}

          {/* Lost-specific fields */}
          {outcome === 'lost' && (
            <div className="space-y-4 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900">Lost Reason</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Reason *
                </label>
                <select
                  value={outcomeData.lostReason}
                  onChange={(e) => setOutcomeData(prev => ({ ...prev, lostReason: e.target.value }))}
                  disabled={submitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
                >
                  <option value="">Select reason...</option>
                  <option value="price_too_high">Price Too High</option>
                  <option value="chose_competitor">Chose Competitor</option>
                  <option value="timeline_too_long">Timeline Too Long</option>
                  <option value="design_not_suitable">Design Not Suitable</option>
                  <option value="changed_mind">Changed Mind/No Longer Needed</option>
                  <option value="unresponsive">Client Unresponsive</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Competitor Name (if applicable)
                </label>
                <input
                  type="text"
                  value={outcomeData.competitorName}
                  onChange={(e) => setOutcomeData(prev => ({ ...prev, competitorName: e.target.value }))}
                  disabled={submitting}
                  placeholder="Enter competitor name if known"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Details
                </label>
                <textarea
                  value={outcomeData.reason}
                  onChange={(e) => setOutcomeData(prev => ({ ...prev, reason: e.target.value }))}
                  disabled={submitting}
                  rows={4}
                  placeholder="Provide any additional context about why the project was lost..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
                />
              </div>
            </div>
          )}

          {/* General Notes (all outcomes) */}
          {outcome && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={outcomeData.notes}
                onChange={(e) => setOutcomeData(prev => ({ ...prev, notes: e.target.value }))}
                disabled={submitting}
                rows={3}
                placeholder="Add any additional notes or observations..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-lg">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !outcome || loadingAppointment || !appointmentId}
            className={`px-6 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
            style={{
              backgroundColor: config
                ? (config.color === 'green' ? '#059669' : config.color === 'yellow' ? '#d97706' : '#dc2626')
                : '#6b7280'
            }}
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Recording...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Record {outcome ? outcome.charAt(0).toUpperCase() + outcome.slice(1) : 'Outcome'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordOutcomeModal;
